export type AppEnvironmentKey = 'dev' | 'qa' | 'uat' | 'prod';
export type EnvironmentLabel =
  | 'Local'
  | 'Development'
  | 'Staging'
  | 'Uat'
  | 'Production';

export type AppEnvironment = {
  label: Exclude<EnvironmentLabel, 'Local'>;
  sentryEnvironment: string;
  tracesSampleRate: number;
};

export const ENTRA_TENANT_ID = '1d063515-6cad-4195-9486-ea65df456faa';

const environmentConfig = {
  dev: {
    label: 'Development',
    sentryEnvironment: 'development',
    tracesSampleRate: 1.0,
  },
  qa: { label: 'Staging', sentryEnvironment: 'staging', tracesSampleRate: 1.0 },
  uat: { label: 'Uat', sentryEnvironment: 'uat', tracesSampleRate: 0.5 },
  prod: {
    label: 'Production',
    sentryEnvironment: 'production',
    tracesSampleRate: 0.2,
  },
} as const;

export const getConfiguredEnvironment = (): AppEnvironment => {
  const env = import.meta.env.VITE_MSAL_ENVIRONMENT ?? 'dev';

  if (!(env in environmentConfig)) {
    throw new Error(`Invalid environment: ${env}`);
  }

  return environmentConfig[env as AppEnvironmentKey];
};

export const getSentryEnvironment = (): string =>
  getConfiguredEnvironment().sentryEnvironment;

export const getSentryTracesSampleRate = (): number =>
  getConfiguredEnvironment().tracesSampleRate;

export const isLocalEnvironment = (): boolean => {
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('local.')
  );
};

export const getEnvironmentLabel = (): EnvironmentLabel => {
  if (isLocalEnvironment()) {
    return 'Local';
  }

  return getConfiguredEnvironment().label;
};

export const getPreviewLabel = (): string | undefined =>
  import.meta.env.VITE_PREVIEW_LABEL || undefined;
